import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  status: "Active",
  notes: "",
};

export default function ClientFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initial,
  title,
}) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(initial || {}) });
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const valid = form.name.trim().length > 0;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title || "Add Client"}</DialogTitle>
          <DialogDescription>
            Fill in the client details. Saved locally for this demo session.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="c-name" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Name</Label>
              <Input id="c-name" data-testid="client-form-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="h-10 rounded-lg" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-company" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Company</Label>
              <Input id="c-company" data-testid="client-form-company" value={form.company} onChange={set("company")} placeholder="Acme Inc." className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-status" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="client-form-status" className="h-10 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-email" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Email</Label>
              <Input id="c-email" type="email" data-testid="client-form-email" value={form.email} onChange={set("email")} placeholder="jane@example.com" className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-phone" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Phone</Label>
              <Input id="c-phone" data-testid="client-form-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="c-notes" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Notes</Label>
              <Textarea id="c-notes" data-testid="client-form-notes" value={form.notes} onChange={set("notes")} rows={3} className="rounded-lg resize-none" placeholder="Anything worth remembering..." />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="client-form-cancel" className="rounded-lg">
              Cancel
            </Button>
            <Button type="submit" disabled={!valid} data-testid="client-form-submit" className="bg-primary hover:bg-primary/90 rounded-lg">
              {initial ? "Save Changes" : "Add Client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
